/**
 * Withdraw review / log — GET /api/admin/withdraw (optional status filter).
 *
 * Approve requires a `payout_ref` (off-chain transfer reference the ops
 * person pasted after paying out). Reject body uses `reason`, which is
 * mirrored back to the user's review_note.
 *
 * Mirrors the shape of RechargeReviewPage so the two finance queues feel
 * consistent to operators.
 */
import { useState } from 'react';
import { Button, Input, Modal, Select, Space, Tag, Tooltip, Typography, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface WithdrawRow {
  id: number;
  user_id: number;
  amount_uo: string;
  amount_cny: string;
  method: string;
  status: 'pending' | 'approved' | 'rejected';
  payout_info: string;
  payout_ref: string | null;
  review_note: string | null;
  reviewed_by: number | null;
  reviewed_at: string | null;
  create_time: string;
  split_status?: string;
}

const STATUS_TAG: Record<string, { color: string; text: string }> = {
  pending: { color: 'orange', text: '待审核' },
  approved: { color: 'green', text: '已通过' },
  rejected: { color: 'red', text: '已拒绝' },
};

export interface WithdrawReviewPageProps {
  /** Default filter: `pending` for 提现审核; omit for 提现记录 (all statuses). */
  defaultStatusFilter?: string;
  title?: string;
}

export function WithdrawReviewPage({
  defaultStatusFilter = 'pending',
  title = '提现审核',
}: WithdrawReviewPageProps = {}) {
  const [status, setStatus] = useState<string | undefined>(defaultStatusFilter);
  const [approveTarget, setApproveTarget] = useState<WithdrawRow | null>(null);
  const [payoutRef, setPayoutRef] = useState('');
  const [rejectTarget, setRejectTarget] = useState<WithdrawRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const hasNode = useAuthStore((s) => s.hasNode);
  const qc = useQueryClient();

  const canApprove = hasNode('platform.withdraw.approve');
  const canReject = hasNode('platform.withdraw.reject');

  async function approve() {
    if (!approveTarget) return;
    try {
      await api.post(`/api/admin/withdraw/${approveTarget.id}/approve`, {
        payout_ref: payoutRef,
      });
      message.success(`已通过提现 #${approveTarget.id}`);
      setApproveTarget(null);
      setPayoutRef('');
      qc.invalidateQueries({ queryKey: ['admin', 'withdraw'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '审核通过失败');
    }
  }

  async function reject() {
    if (!rejectTarget) return;
    try {
      await api.post(`/api/admin/withdraw/${rejectTarget.id}/reject`, {
        reason: rejectReason,
      });
      message.success(`已拒绝提现 #${rejectTarget.id}`);
      setRejectTarget(null);
      setRejectReason('');
      qc.invalidateQueries({ queryKey: ['admin', 'withdraw'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '拒绝失败');
    }
  }

  async function confirmSplit(row: WithdrawRow) {
    try {
      await api.post(`/api/admin/withdraw/${row.id}/confirm-split`, {});
      message.success(`已确认拆分 #${row.id}`);
      qc.invalidateQueries({ queryKey: ['admin', 'withdraw'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '拆分失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户', dataIndex: 'user_id', width: 100 },
    {
      title: 'ORB',
      dataIndex: 'amount_uo',
      width: 140,
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    {
      title: '应付 CNY',
      dataIndex: 'amount_cny',
      width: 140,
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    { title: '方式', dataIndex: 'method', width: 110 },
    {
      title: '收款信息',
      dataIndex: 'payout_info',
      ellipsis: true,
      render: (v: string) => (
        <Tooltip title={v}>
          <span style={{ maxWidth: 220, display: 'inline-block' }}>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (v: string) => {
        const t = STATUS_TAG[v] ?? { color: 'default', text: v };
        return <Tag color={t.color}>{t.text}</Tag>;
      },
    },
    {
      title: '打款凭证',
      dataIndex: 'payout_ref',
      width: 160,
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    { title: '提交时间', dataIndex: 'create_time', width: 170 },
    {
      title: '拆分',
      dataIndex: 'split_status',
      width: 90,
      render: (v: string | undefined) => v ?? '—',
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, row: WithdrawRow) => {
        if (row.status === 'pending') {
          return (
            <Space>
              {canApprove && (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    setApproveTarget(row);
                    setPayoutRef('');
                  }}
                >
                  通过
                </Button>
              )}
              {canReject && (
                <Button size="small" danger onClick={() => setRejectTarget(row)}>
                  拒绝
                </Button>
              )}
            </Space>
          );
        }
        if (
          row.status === 'approved' &&
          row.split_status !== 'confirmed' &&
          canApprove
        ) {
          return (
            <Button size="small" type="link" onClick={() => confirmSplit(row)}>
              确认拆分
            </Button>
          );
        }
        return '—';
      },
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <Space style={{ marginBottom: 12 }}>
        <Select
          value={status}
          style={{ width: 160 }}
          onChange={setStatus}
          allowClear
          placeholder="状态"
          options={[
            { value: 'pending', label: '待审核' },
            { value: 'approved', label: '已通过' },
            { value: 'rejected', label: '已拒绝' },
          ]}
        />
      </Space>

      <DataTable<WithdrawRow>
        queryKey={['admin', 'withdraw', status ?? 'all']}
        endpoint="/api/admin/withdraw"
        extraParams={status !== undefined && status !== null ? { status } : {}}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1500 }}
      />

      <Modal
        open={approveTarget !== null}
        title={`通过提现 #${approveTarget?.id}`}
        onOk={approve}
        onCancel={() => {
          setApproveTarget(null);
          setPayoutRef('');
        }}
        okText="确认通过"
        okButtonProps={{ disabled: !payoutRef }}
      >
        <p style={{ marginTop: 0 }}>
          请输入链下打款凭证（交易哈希 / 银行流水号 / 微信转账单号），该凭证将记录到流水中。
        </p>
        <Input
          placeholder="payout_ref"
          value={payoutRef}
          onChange={(e) => setPayoutRef(e.target.value)}
        />
      </Modal>

      <Modal
        open={rejectTarget !== null}
        title={`拒绝提现 #${rejectTarget?.id}`}
        onOk={reject}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason('');
        }}
        okText="确认拒绝"
        okButtonProps={{ danger: true, disabled: !rejectReason }}
      >
        <p style={{ marginTop: 0 }}>
          拒绝后将退回 {rejectTarget?.amount_uo} ORB 到用户账户，该原因将对用户可见。
        </p>
        <Input.TextArea
          rows={3}
          placeholder="拒绝原因（用户可见）"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
